import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/pos`;

const GRNPaymentHistory = () => {
  const [grns, setGrns] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [fromDate, setFromDate] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 90); return d.toISOString().split('T')[0]; });
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [summary, setSummary] = useState(null);
  const [expandedGRN, setExpandedGRN] = useState(null);
  const [showPayModal, setShowPayModal] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', payment_mode: 'cash', notes: '' });
  const [saving, setSaving] = useState(false);

  useEffect(() => { fetchSuppliers(); }, []);
  useEffect(() => { fetchData(); }, [selectedSupplier, fromDate, toDate]);

  const fetchSuppliers = async () => {
    try {
      const token = localStorage.getItem('pos_token');
      const resp = await axios.get(`${API}/suppliers`, { headers: { Authorization: `Bearer ${token}` } });
      setSuppliers(resp.data || []);
    } catch (e) { console.error(e); }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('pos_token');
      const params = {};
      if (selectedSupplier) params.supplier_id = selectedSupplier;
      if (fromDate) params.from_date = fromDate;
      if (toDate) params.to_date = toDate;
      const resp = await axios.get(`${API}/grn-payment-history`, { headers: { Authorization: `Bearer ${token}` }, params });
      setGrns(resp.data.grns || []);
      setSummary(resp.data.summary || null);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };

  const recordPayment = async () => {
    if (!payForm.amount || parseFloat(payForm.amount) <= 0) return;
    setSaving(true);
    try {
      const token = localStorage.getItem('pos_token');
      await axios.post(`${API}/grn/${showPayModal.id}/record-payment`, null, {
        headers: { Authorization: `Bearer ${token}` },
        params: { amount: parseFloat(payForm.amount), payment_mode: payForm.payment_mode, notes: payForm.notes }
      });
      setShowPayModal(null);
      setPayForm({ amount: '', payment_mode: 'cash', notes: '' });
      fetchData();
    } catch (err) {
      alert(err.response?.data?.detail || 'Failed to record payment');
    } finally { setSaving(false); }
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '-';

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div></div>;

  return (
    <div data-testid="grn-payment-history-page" className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-800">GRN Payment History</h1>
        <p className="text-slate-500">Track payments against purchase entries</p>
      </div>

      {summary && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-xs text-slate-500">Total GRNs</p><p className="text-xl font-bold text-slate-800">{summary.total_grns}</p></div>
          <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-xs text-slate-500">Total Amount</p><p className="text-xl font-bold text-slate-800">Rs.{(summary.total_amount || 0).toFixed(2)}</p></div>
          <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-xs text-slate-500">Total Paid</p><p className="text-xl font-bold text-emerald-600">Rs.{(summary.total_paid || 0).toFixed(2)}</p></div>
          <div className="bg-white rounded-xl border border-slate-200 p-4"><p className="text-xs text-slate-500">Balance Due</p><p className="text-xl font-bold text-red-600">Rs.{(summary.total_balance || 0).toFixed(2)}</p></div>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-4">
        <div className="flex flex-wrap items-center gap-3">
          <select value={selectedSupplier} onChange={e => setSelectedSupplier(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm min-w-[180px]">
            <option value="">All Suppliers</option>
            {suppliers.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
          <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
          <span className="text-slate-400">to</span>
          <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="px-3 py-2 border border-slate-200 rounded-lg text-sm" />
        </div>
      </div>

      <div className="space-y-3">
        {grns.length === 0 ? (
          <div className="bg-white rounded-xl border border-slate-200 p-8 text-center text-slate-400">No GRN records found</div>
        ) : grns.map(grn => (
          <div key={grn.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
            <div className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-50" onClick={() => setExpandedGRN(expandedGRN === grn.id ? null : grn.id)}>
              <div className="flex-1">
                <div className="flex items-center gap-3">
                  <p className="font-semibold text-slate-800">{grn.grn_number}</p>
                  <span className={`px-2 py-0.5 text-xs rounded font-medium ${
                    grn.payment_status === 'paid' ? 'bg-emerald-100 text-emerald-700' :
                    grn.payment_status === 'partial' ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'
                  }`}>{grn.payment_status || 'credit'}</span>
                  {grn.has_returns && <span className="px-2 py-0.5 text-xs rounded bg-orange-100 text-orange-700">Has Returns</span>}
                </div>
                <p className="text-sm text-slate-500">{grn.supplier_name} | Inv: {grn.invoice_number || '-'} | {formatDate(grn.created_at)}</p>
              </div>
              <div className="text-right">
                <p className="text-sm text-slate-500">Amount: <strong>Rs.{(grn.net_amount || grn.total_amount || 0).toFixed(2)}</strong></p>
                <p className="text-sm">Paid: <strong className="text-emerald-600">Rs.{(grn.total_paid || 0).toFixed(2)}</strong> | Due: <strong className="text-red-600">Rs.{Math.max(0, grn.balance_due || 0).toFixed(2)}</strong></p>
              </div>
            </div>
            {expandedGRN === grn.id && (
              <div className="border-t border-slate-200 p-4 bg-slate-50">
                <div className="flex items-center justify-between mb-3">
                  <h4 className="font-semibold text-sm text-slate-700">Payment History</h4>
                  {(grn.balance_due || 0) > 0 && (
                    <button onClick={(e) => { e.stopPropagation(); setShowPayModal(grn); setPayForm({ amount: grn.balance_due.toFixed(2), payment_mode: 'cash', notes: '' }); }}
                      className="px-3 py-1.5 bg-emerald-600 text-white text-xs rounded-lg font-medium hover:bg-emerald-700" data-testid={`record-payment-${grn.id}`}>
                      Record Payment
                    </button>
                  )}
                </div>
                {(grn.payments || []).length === 0 ? (
                  <p className="text-sm text-slate-400">No payments recorded yet</p>
                ) : (
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-slate-200"><th className="text-left py-1 text-xs text-slate-500">Date</th><th className="text-left py-1 text-xs text-slate-500">Mode</th><th className="text-right py-1 text-xs text-slate-500">Amount</th><th className="text-left py-1 text-xs text-slate-500">Notes</th></tr></thead>
                    <tbody>
                      {grn.payments.map((p, i) => (
                        <tr key={i} className="border-b border-slate-100">
                          <td className="py-1.5 text-xs">{formatDate(p.created_at)}</td>
                          <td className="py-1.5"><span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded text-xs capitalize">{p.payment_mode || '-'}</span></td>
                          <td className="py-1.5 text-right font-medium text-emerald-600">Rs.{(p.amount || 0).toFixed(2)}</td>
                          <td className="py-1.5 text-xs text-slate-500">{p.notes || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
          </div>
        ))}
      </div>

      {showPayModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-sm">
            <div className="p-4 border-b border-slate-200">
              <h3 className="font-semibold">Record Payment - {showPayModal.grn_number}</h3>
              <p className="text-sm text-slate-500">Balance: Rs.{(showPayModal.balance_due || 0).toFixed(2)}</p>
            </div>
            <div className="p-4 space-y-3">
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Amount</label>
                <input type="number" value={payForm.amount} onChange={e => setPayForm({...payForm, amount: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg" data-testid="pay-amount" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Mode</label>
                <select value={payForm.payment_mode} onChange={e => setPayForm({...payForm, payment_mode: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg">
                  <option value="cash">Cash</option><option value="bank">Bank</option><option value="upi">UPI</option><option value="cheque">Cheque</option>
                </select></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1">Notes</label>
                <input type="text" value={payForm.notes} onChange={e => setPayForm({...payForm, notes: e.target.value})} className="w-full px-3 py-2 border border-slate-200 rounded-lg" placeholder="Payment reference..." /></div>
              <div className="flex gap-2">
                <button onClick={() => setShowPayModal(null)} className="flex-1 py-2 border border-slate-200 rounded-lg text-sm">Cancel</button>
                <button onClick={recordPayment} disabled={saving} className="flex-1 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium disabled:opacity-50" data-testid="confirm-payment">{saving ? 'Saving...' : 'Record Payment'}</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GRNPaymentHistory;
